import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, type AuditEventRow } from "../../lib/api";
import { Alert, AlertDescription, AlertTitle } from "../../app/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { useAuth } from "../../providers/AuthProvider";
import { TableLayout, type TableColumn } from "../../components/dashboard";

function formatAction(a: AuditEventRow["action"]) {
  switch (a) {
    case "business_verified":
      return "Business verified";
    case "business_rejected":
      return "Business rejected";
    case "impersonation_started":
      return "Impersonation started";
    case "impersonation_ended":
      return "Impersonation ended";
    default:
      return a;
  }
}

export function AuditLogPage() {
  const { token } = useAuth();
  const [rows, setRows] = useState<AuditEventRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setError(null);
      setLoading(true);
      try {
        const list = await api.admin.listAuditEvents({ token, limit: 100 });
        if (!cancelled) setRows(list);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? "Failed to load audit log");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const columns: TableColumn<AuditEventRow>[] = useMemo(
    () => [
      {
        key: "time",
        header: "Time",
        render: (r) => (
          <span className="whitespace-nowrap text-muted-foreground">{new Date(r.timestamp).toLocaleString()}</span>
        ),
      },
      { key: "action", header: "Action", render: (r) => <span className="font-medium">{formatAction(r.action)}</span> },
      { key: "business", header: "Business", render: (r) => r.businessName ?? "—" },
      {
        key: "actor",
        header: "Actor",
        render: (r) => (
          <div className="min-w-0 leading-tight">
            <div>{r.actorName}</div>
            <div className="text-xs text-muted-foreground">{r.actorEmail}</div>
          </div>
        ),
      },
      { key: "target", header: "Target user", render: (r) => <span className="text-muted-foreground">{r.targetUserId ?? "—"}</span> },
    ],
    [],
  );

  const mobileCardRender = useCallback((r: AuditEventRow) => (
    <div className="space-y-2 text-sm">
      <div className="text-xs text-muted-foreground">{new Date(r.timestamp).toLocaleString()}</div>
      <div className="font-medium">{formatAction(r.action)}</div>
      <div>Business: {r.businessName ?? "—"}</div>
      <div>
        Actor: {r.actorName} ({r.actorEmail})
      </div>
      <div className="text-muted-foreground">Target: {r.targetUserId ?? "—"}</div>
    </div>
  ), []);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Audit log</h1>
        <p className="text-muted-foreground">Recent super admin actions (mock data).</p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Events</CardTitle>
          <CardDescription>Newest first</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No events yet.</div>
          ) : (
            <div className="min-w-0 rounded-md border md:border-0">
              <TableLayout columns={columns} rows={rows} mobileCardRender={mobileCardRender} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
